/**
 * Revit → ontology mapping for LA County / Casita-style models (decimal feet).
 * Ported from packages/rules/scripts/run-casita.ts for browser-side live extraction.
 */
import type { EvaluatedElement, QuantityValue } from "@vera/rules";

const FT_TO_MM = 304.8;

/**
 * Revit length parameters: US residential models use decimal feet; many
 * Singapore / commercial ACC models use millimetres in APS properties.
 */
function lengthToMm(raw: string | undefined): QuantityValue | undefined {
  if (raw == null || raw === "") return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  if (n >= 25) return { value: Math.round(n), unit: "millimetre" };
  return { value: Math.round(n * FT_TO_MM), unit: "millimetre" };
}

/** Room area: decimal sq ft (US) vs square metres (metric). */
function areaToSquareMetre(raw: string | undefined): QuantityValue | undefined {
  if (raw == null || raw === "") return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  if (n <= 100)
    return { value: Math.round(n * 1000) / 1000, unit: "squareMetre" };
  return {
    value: Math.round(n * 0.092903 * 1000) / 1000,
    unit: "squareMetre",
  };
}

export interface RawViewerElement {
  dbId: number;
  name?: string;
  category?: string;
  properties: {
    displayName: string;
    displayCategory: string;
    displayValue: string;
  }[];
}

function prop(e: RawViewerElement, name: string): string | undefined {
  return e.properties.find(p => p.displayName === name)?.displayValue;
}

const HABITABLE_USES = new Set([
  "bedroom",
  "living room",
  "dining room",
  "den",
  "family room",
  "study",
  "office",
]);

function classifyRoomUse(name: string | undefined): string | undefined {
  const s = (name ?? "").toLowerCase();
  if (/bed/.test(s)) return "bedroom";
  if (/living/.test(s)) return "living room";
  if (/din/.test(s)) return "dining room";
  if (/den|family|study|office/.test(s)) return "den";
  if (/kitchen/.test(s)) return "kitchen";
  if (/bath/.test(s)) return "bathroom";
  if (/laundry/.test(s)) return "laundry room";
  if (/clo\b|closet|walk-?in/.test(s)) return "closet";
  if (/pantry/.test(s)) return "pantry";
  if (/mudroom/.test(s)) return "mudroom";
  if (/vestibule/.test(s)) return "vestibule";
  if (/porch/.test(s)) return "porch";
  return undefined;
}

interface RoomMeta {
  name: string;
  use?: string;
  hostsSleepingRoom: boolean;
}

function buildRoomIndex(
  elements: Record<string, RawViewerElement>
): Map<string, RoomMeta> {
  const byNumber = new Map<string, RoomMeta>();
  for (const e of Object.values(elements)) {
    if (e.category !== "Revit Rooms") continue;
    const name = prop(e, "Name") ?? e.name ?? "";
    const number = prop(e, "Number") ?? "";
    const use = classifyRoomUse(name);
    const meta: RoomMeta = {
      name,
      use,
      hostsSleepingRoom: use === "bedroom",
    };
    if (number) byNumber.set(number, meta);
    if (name) byNumber.set(name, meta);
  }
  return byNumber;
}

function resolveHostRoomUse(
  window: RawViewerElement,
  roomIndex: Map<string, RoomMeta>
): string | undefined {
  const roomNumber = prop(window, "Room Number");
  const roomName = prop(window, "Room Name");
  const meta =
    (roomNumber ? roomIndex.get(roomNumber) : undefined) ||
    (roomName ? roomIndex.get(roomName) : undefined);
  return meta?.use;
}

function scoreEgressDoor(
  e: RawViewerElement,
  clearWidth: QuantityValue
): number {
  const name = (e.name ?? "").toLowerCase();
  let score = clearWidth.value;
  if (/entry|front|main|egress/.test(name)) score += 500;
  if (/garage|closet|pantry|bath/.test(name)) score -= 300;
  return score;
}

function pickEgressDoorDbId(
  candidates: { dbId: number; score: number }[]
): number | undefined {
  if (candidates.length === 0) return undefined;
  return candidates.sort((a, b) => b.score - a.score)[0]?.dbId;
}

function pickRequiredEeroDbIds(
  windows: Array<{
    dbId: number;
    hostKey: string;
    hostUse?: string;
    sillMm: number;
  }>
): Set<number> {
  const chosen = new Set<number>();
  const byRoom = new Map<
    string,
    Array<{ dbId: number; hostUse?: string; sillMm: number }>
  >();
  for (const w of windows) {
    if (w.hostUse !== "bedroom") continue;
    const list = byRoom.get(w.hostKey) ?? [];
    list.push(w);
    byRoom.set(w.hostKey, list);
  }
  for (const list of byRoom.values()) {
    const best = list.sort((a, b) => a.sillMm - b.sillMm)[0];
    if (best) chosen.add(best.dbId);
  }
  return chosen;
}

/** Map extracted Revit properties to engine EvaluatedElements (LA / Casita profile). */
export function buildLaCountyEvaluatedElements(
  elements: Record<string, RawViewerElement>
): EvaluatedElement[] {
  const roomIndex = buildRoomIndex(elements);
  const out: EvaluatedElement[] = [];

  const doorCandidates: { dbId: number; score: number }[] = [];
  const windowMeta: Array<{
    dbId: number;
    hostKey: string;
    hostUse?: string;
    sillMm: number;
  }> = [];

  for (const e of Object.values(elements)) {
    switch (e.category) {
      case "Revit Doors": {
        const clearSrc = prop(e, "Clear Width") ? "Clear Width" : "Rough Width";
        const clearWidth = lengthToMm(prop(e, clearSrc));
        if (!clearWidth) break;
        doorCandidates.push({
          dbId: e.dbId,
          score: scoreEgressDoor(e, clearWidth),
        });
        break;
      }
      case "Revit Windows": {
        const sillHeight = lengthToMm(prop(e, "Sill Height"));
        if (!sillHeight) break;
        const hostUse = resolveHostRoomUse(e, roomIndex);
        const hostKey =
          prop(e, "Room Number") ?? prop(e, "Room Name") ?? `dbId-${e.dbId}`;
        windowMeta.push({
          dbId: e.dbId,
          hostKey,
          hostUse,
          sillMm: sillHeight.value,
        });
        break;
      }
      default:
        break;
    }
  }

  const egressDoorDbId = pickEgressDoorDbId(doorCandidates);
  const eeroDbIds = pickRequiredEeroDbIds(windowMeta);

  for (const e of Object.values(elements)) {
    switch (e.category) {
      case "Revit Doors": {
        const clearSrc = prop(e, "Clear Width") ? "Clear Width" : "Rough Width";
        const clearWidth = lengthToMm(prop(e, clearSrc));
        if (!clearWidth) break;
        const isEgress = e.dbId === egressDoorDbId;
        out.push({
          dbId: e.dbId,
          ontologyType: "vera:Door",
          displayName: e.name,
          facts: {
            clearWidth,
            openingHeight: lengthToMm(
              prop(e, "Clear Height") ?? prop(e, "Rough Height")
            ),
            isOnRequiredMeansOfEgress: isEgress,
          },
          factSources: {
            clearWidth: `Live Revit Doors → ${clearSrc}`,
            isOnRequiredMeansOfEgress: isEgress
              ? "Heuristic: designated egress door"
              : "Heuristic: not the designated egress door",
          },
        });
        break;
      }
      case "Revit Runs": {
        const clearWidth = lengthToMm(prop(e, "Actual Run Width"));
        const riserHeight = lengthToMm(prop(e, "Actual Riser Height"));
        const treadDepth = lengthToMm(prop(e, "Actual Tread Depth"));
        const verticalRise = lengthToMm(
          prop(e, "Run Height") ?? prop(e, "Relative Top Height")
        );
        if (!clearWidth && !riserHeight && !treadDepth && !verticalRise) break;
        out.push({
          dbId: e.dbId,
          ontologyType: "vera:Stair",
          displayName: e.name,
          facts: {
            clearWidth,
            riserHeight,
            treadDepth,
            verticalRise,
            isOnRequiredMeansOfEgress: true,
          },
          factSources: {
            isOnRequiredMeansOfEgress:
              "Heuristic: stair run treated as required egress",
          },
        });
        break;
      }
      case "Revit Windows": {
        const sillHeight = lengthToMm(prop(e, "Sill Height"));
        if (!sillHeight) break;
        const hostUse = resolveHostRoomUse(e, roomIndex);
        const isRequiredEero = eeroDbIds.has(e.dbId);
        const headHeight = lengthToMm(prop(e, "Head Height"));
        const openingHeight =
          headHeight && sillHeight
            ? {
                value: Math.max(0, headHeight.value - sillHeight.value),
                unit: "millimetre" as const,
              }
            : undefined;
        out.push({
          dbId: e.dbId,
          ontologyType: "vera:Opening",
          displayName: e.name,
          facts: {
            sillHeight,
            openingHeight,
            ...(hostUse ? { hostRoomUse: hostUse } : {}),
            isRequiredEero,
          },
          factSources: {
            sillHeight: 'Live Revit Windows → "Sill Height"',
            isRequiredEero: isRequiredEero
              ? "Heuristic: designated EERO for sleeping room"
              : "Heuristic: EERO rules do not apply",
          },
        });
        break;
      }
      case "Revit Rooms": {
        const use = classifyRoomUse(prop(e, "Name") ?? e.name);
        const habitable = use ? HABITABLE_USES.has(use) : false;
        const floorArea =
          habitable || use === "kitchen"
            ? areaToSquareMetre(prop(e, "Area"))
            : undefined;
        const ceilingHeight = lengthToMm(prop(e, "Unbounded Height"));
        if (!floorArea && !ceilingHeight && !use) break;
        const facts: EvaluatedElement["facts"] = {
          floorArea,
          ceilingHeight,
        };
        if (use) facts.roomUse = use;
        if (use === "bedroom") facts.hostsSleepingRoom = true;
        out.push({
          dbId: e.dbId,
          ontologyType: "vera:Room",
          displayName: e.name,
          facts,
          factSources: {
            ...(use && { roomUse: `Live Revit Room name → "${use}"` }),
          },
        });
        break;
      }
      default:
        break;
    }
  }
  return out;
}
